/**
 * 密码策略（与后端 PasswordPolicyService / PasswordConstants 一致）。
 */

export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 64;

export const PASSWORD_RULE_HINT =
    '至少 8 位，须含大写字母、小写字母和数字，不可使用常见弱口令，且不能与登录名相同';

/** 常见弱口令（小写比对） */
export const WEAK_PASSWORDS = new Set([
    '123456',
    '12345678',
    '123456789',
    'password',
    'password1',
    'admin123',
    'qwerty',
    'qwerty123',
    'abc123',
    '111111',
    '000000',
    '654321',
    '123123',
    '1234567890',
    'iloveyou',
    'welcome',
    'monkey',
    'dragon',
    'master',
    'letmein',
    'login',
    'passw0rd',
    'admin',
    'root',
    'test123',
    '88888888',
    '666666',
    'a1234567',
    'aa123456',
    'qwer1234',
]);

/**
 * @param {string} password
 * @param {string} [loginId]
 * @returns {{ valid: boolean, message?: string }}
 */
export function validatePassword(password, loginId) {
    if (!password || !String(password).trim()) {
        return { valid: false, message: '请输入密码' };
    }
    const pwd = String(password);
    if (pwd.length < PASSWORD_MIN_LENGTH || pwd.length > PASSWORD_MAX_LENGTH) {
        return { valid: false, message: '密码长度为 8–64 个字符' };
    }
    if (!/[A-Z]/.test(pwd)) {
        return { valid: false, message: PASSWORD_RULE_HINT };
    }
    if (!/[a-z]/.test(pwd)) {
        return { valid: false, message: PASSWORD_RULE_HINT };
    }
    if (!/[0-9]/.test(pwd)) {
        return { valid: false, message: PASSWORD_RULE_HINT };
    }
    if (WEAK_PASSWORDS.has(pwd.toLowerCase())) {
        return { valid: false, message: PASSWORD_RULE_HINT };
    }
    if (loginId && pwd.toLowerCase() === String(loginId).trim().toLowerCase()) {
        return { valid: false, message: '密码不能与登录名相同' };
    }
    return { valid: true };
}

/**
 * 估算强度用于 Progress 展示：0–100
 * @param {string} password
 */
export function scorePasswordStrength(password) {
    if (!password) {
        return 0;
    }
    let score = 0;
    if (password.length >= 8) {
        score += 25;
    }
    if (password.length >= 12) {
        score += 15;
    }
    if (/[A-Z]/.test(password)) {
        score += 20;
    }
    if (/[a-z]/.test(password)) {
        score += 20;
    }
    if (/[0-9]/.test(password)) {
        score += 20;
    }
    if (/[^a-zA-Z0-9]/.test(password)) {
        score += 10;
    }
    if (WEAK_PASSWORDS.has(password.toLowerCase())) {
        return 10;
    }
    return Math.min(100, score);
}

/** Ant Design Form 异步校验器 */
export function passwordFormValidator(loginIdGetter) {
    return (_, value) => {
        if (!value || !String(value).trim()) {
            return Promise.resolve();
        }
        const loginId = typeof loginIdGetter === 'function' ? loginIdGetter() : loginIdGetter;
        const result = validatePassword(value, loginId);
        if (!result.valid) {
            return Promise.reject(new Error(result.message));
        }
        return Promise.resolve();
    };
}

/** 创建用户等必填密码场景 */
export function passwordRequiredValidator(loginIdGetter) {
    return (_, value) => {
        if (!value || !String(value).trim()) {
            return Promise.reject(new Error('请输入密码'));
        }
        const loginId = typeof loginIdGetter === 'function' ? loginIdGetter() : loginIdGetter;
        const result = validatePassword(value, loginId);
        if (!result.valid) {
            return Promise.reject(new Error(result.message));
        }
        return Promise.resolve();
    };
}
